//@flow
import * as React from 'react';
import classnames from 'classnames';
import { themr } from 'react-css-themr';
import theme from './PjpChartsCustomLegend.scss';
import Icon from 'common/components/core/icon';
import StringUtils from 'common/utils/StringUtils';

type Props = {
    width?: number,
    height?: number,
    verticalAlign?: 'top' | 'middle' | 'bottom',
    align?: 'left' | 'center' | 'right',
    iconSize?: number,
    iconType?:
        | 'line'
        | 'square'
        | 'rect'
        | 'circle'
        | 'cross'
        | 'diamond'
        | 'star'
        | 'triangle'
        | 'wye',
    payload: Array<Object>,
    layout?: 'horizontal' | 'vertical',
    margin?: { top: number, left: number, right: number, bottom: number },
    content?:
        | React.Element<React.ComponentType<PjpChartsCustomLegend>>
        | (() => React.Element<React.ComponentType<PjpChartsCustomLegend>>),
    wrapperStyle?: Object,
    hoverableLegend?: boolean,
    onClick?: () => void,
    onMouseDown?: () => void,
    onMouseUp?: () => void,
    onMouseMove?: () => void,
    onMouseOver?: () => void,
    onMouseOut?: () => void,
    onMouseEnter?: () => void,
    onMouseLeave?: () => void,
    onItemMouseEnter?: Object => void,
    onItemMouseLeave?: Object => void
};

type State = {
    visiblePage: number
};

const itemsPerPage = 4;

class PjpChartsCustomLegend extends React.PureComponent<Props, State> {
    state: State = {
        visiblePage: 1
    };

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (nextProps.payload.length !== this.props.payload.length) {
            //TODO: Untested logic
            const { visiblePage } = this.state;
            const maxPageWithNewPayload = Math.ceil(
                nextProps.payload.length / itemsPerPage
            );
            if (visiblePage > maxPageWithNewPayload) {
                this.setState({ visiblePage: maxPageWithNewPayload });
            }
        }
    }

    getMouseOverHandler(item: Object) {
        const { onItemMouseEnter } = this.props;
        if (onItemMouseEnter) {
            return onItemMouseEnter.bind(this, item);
        }
    }

    getMouseLeaveHandler(item: Object) {
        const { onItemMouseLeave } = this.props;
        if (onItemMouseLeave) {
            return onItemMouseLeave.bind(this, item);
        }
    }

    goToPrevPage = () => {
        this.setState((prevState: State) => ({
            visiblePage: prevState.visiblePage - 1
        }));
    };

    goToNextPage = () => {
        this.setState((prevState: State) => ({
            visiblePage: prevState.visiblePage + 1
        }));
    };

    renderPage(pageNo: number, itemsPerPage: number) {
        const {
            payload,
            onItemMouseEnter,
            onItemMouseLeave,
            hoverableLegend
        } = this.props;
        const pageElements = [];
        for (
            var i = itemsPerPage * (pageNo - 1);
            i < Math.min(itemsPerPage * pageNo, payload.length);
            i++
        ) {
            const item = payload[i];
            let mouseHandlers = {};
            if (onItemMouseEnter && onItemMouseLeave) {
                mouseHandlers = {
                    onMouseEnter: this.getMouseOverHandler(item),
                    onMouseLeave: this.getMouseLeaveHandler(item)
                };
            }
            pageElements.push(
                <div
                    key={item.index}
                    className={classnames(theme.legendItem, {
                        [theme.hoverable]: hoverableLegend
                    })}
                    {...mouseHandlers}
                >
                    <div className={theme.legendItemInner}>
                        <div
                            className={theme.itemColor}
                            style={{ backgroundColor: item.color }}
                        />
                        {!StringUtils.isEmpty(item.value) ? (
                            <div
                                className={classnames(
                                    theme.bold,
                                    theme.itemValue
                                )}
                            >
                                {item.value}
                                &nbsp;
                            </div>
                        ) : null}
                        <div title={item.name} className={theme.itemName}>
                            {item.name}
                        </div>
                    </div>
                </div>
            );
        }
        const maxPageNo = Math.ceil(payload.length / itemsPerPage);
        const navigationElement =
            maxPageNo !== 1 ? (
                <div className={theme.legendNavigation} key="navigation">
                    <Icon
                        theme={theme}
                        className={classnames(
                            theme.navigationIcon,
                            'draggableCancel',
                            {
                                [theme.disabled]: pageNo === 1
                            }
                        )}
                        icon="caret_left"
                        onClick={pageNo === 1 ? null : this.goToPrevPage}
                    />
                    &nbsp;
                    <span>
                        {pageNo}/{maxPageNo}
                    </span>
                    &nbsp;
                    <Icon
                        theme={theme}
                        className={classnames(
                            theme.navigationIcon,
                            'draggableCancel',
                            {
                                [theme.disabled]: pageNo === maxPageNo
                            }
                        )}
                        icon="caret_right"
                        onClick={
                            pageNo === maxPageNo ? null : this.goToNextPage
                        }
                    />
                </div>
            ) : null;
        return [
            pageElements.length > 0 ? (
                <div className={theme.listWrap} key="list">
                    {pageElements}
                </div>
            ) : null,
            navigationElement
        ];
    }

    getItemsPerPage = () => {
        const { height, payload } = this.props;
        const heightPerItem = 27;
        const heightOffset = 30 + 20 + 12 + 4;
        // height of pagination buttons + padding + padding from chart edge;
        let itemsPerPage = 4;
        if (height) {
            const legendHeight = height - heightOffset;
            itemsPerPage = Math.floor(legendHeight / heightPerItem);
        }
        if (itemsPerPage > payload.length) {
            itemsPerPage = payload.length;
        }
        return itemsPerPage > 0 ? itemsPerPage : 1;
    };

    render() {
        const { align, verticalAlign, layout, payload } = this.props;
        const { visiblePage } = this.state;
        const hAlignClass = align ? `${align}Align` : null;
        const vAlignClass = verticalAlign ? `${verticalAlign}Align` : null;
        const layoutClass = layout ? `${layout}Layout` : null;
        const itemsPerPage = this.getItemsPerPage();
        const maxPageNo = Math.ceil(payload.length / itemsPerPage);
        return (
            <div
                className={classnames(
                    theme.legendWrap,
                    theme[vAlignClass],
                    theme[hAlignClass],
                    theme[layoutClass],
                    {
                        [theme.multiPage]: maxPageNo > 1
                    }
                )}
            >
                <div className={theme.list}>
                    {this.renderPage(visiblePage, itemsPerPage)}
                </div>
            </div>
        );
    }
}

export default themr('PjpChartsCustomLegend', theme)(PjpChartsCustomLegend);
